import { useParams, useNavigate, Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import {
  ArrowLeft,
  ShoppingCart,
  CreditCard,
  User,
  Calendar,
  Mail,
  Phone,
  Package,
  CheckCircle,
  Clock,
  XCircle,
  Copy,
} from 'lucide-react';
import { motion } from 'framer-motion';
import toast from 'react-hot-toast';
import { cn, formatDate, formatDateTime, formatCurrency, getStatusColor, copyToClipboard } from '@utils';
import api from '@services/api';
import type { Order } from '@types';

export default function OrderDetailPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();

  const { data, isLoading } = useQuery({
    queryKey: ['order', id],
    queryFn: () => api.get<{ data: Order }>(`/orders/${id}`),
    enabled: !!id,
  });

  const order = data?.data;

  if (isLoading) {
    return (
      <div className="space-y-6 animate-pulse">
        <div className="flex items-center gap-4">
          <div className="w-10 h-10 bg-gray-200 rounded-lg dark:bg-gray-700" />
          <div className="h-8 bg-gray-200 rounded w-48 dark:bg-gray-700" />
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
          {[1, 2, 3].map(i => <div key={i} className="h-24 bg-gray-200 rounded-xl dark:bg-gray-700" />)}
        </div>
        <div className="h-96 bg-gray-200 rounded-xl dark:bg-gray-700" />
      </div>
    );
  }

  if (!order) {
    return (
      <div className="text-center py-12">
        <h2 className="text-xl font-semibold text-gray-900 dark:text-white">Order not found</h2>
        <Link to="/orders" className="btn-primary mt-4 inline-flex">Back to Orders</Link>
      </div>
    );
  }

  return (
    <motion.div
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      className="space-y-6"
    >
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div className="flex items-center gap-4">
          <button
            onClick={() => navigate('/orders')}
            className="p-2 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-800"
            aria-label="Back to Orders"
          >
            <ArrowLeft className="w-5 h-5" />
          </button>
          <div>
            <div className="flex items-center gap-3">
              <h1 className="text-2xl font-bold text-gray-900 dark:text-white">
                Order #{order.orderNumber}
              </h1>
              <span className={cn('badge', getStatusColor(order.status))}>{order.status}</span>
            </div>
            <p className="text-gray-500 dark:text-gray-400 mt-1">
              Placed {formatDateTime(order.createdAt)}
            </p>
          </div>
        </div>
        <button
          onClick={() => {
            copyToClipboard(order.orderNumber);
            toast.success('Order number copied');
          }}
          className="btn-secondary"
        >
          <Copy className="w-4 h-4" />
          Copy Order #
        </button>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        <div className="card p-4">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-lg bg-success-50 dark:bg-success-900/20 flex items-center justify-center">
              <CreditCard className="w-5 h-5 text-success-600 dark:text-success-400" />
            </div>
            <div>
              <p className="text-sm text-gray-500 dark:text-gray-400">Total</p>
              <p className="text-lg font-bold text-gray-900 dark:text-white">
                {formatCurrency(order.totalAmount, order.currency)}
              </p>
            </div>
          </div>
        </div>
        <div className="card p-4">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-lg bg-primary-50 dark:bg-primary-900/20 flex items-center justify-center">
              <ShoppingCart className="w-5 h-5 text-primary-600 dark:text-primary-400" />
            </div>
            <div>
              <p className="text-sm text-gray-500 dark:text-gray-400">Payment</p>
              <p className="text-lg font-bold text-gray-900 dark:text-white capitalize">{order.paymentStatus}</p>
            </div>
          </div>
        </div>
        <div className="card p-4">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-lg bg-warning-50 dark:bg-warning-900/20 flex items-center justify-center">
              <Package className="w-5 h-5 text-warning-600 dark:text-warning-400" />
            </div>
            <div>
              <p className="text-sm text-gray-500 dark:text-gray-400">Fulfillment</p>
              <p className="text-lg font-bold text-gray-900 dark:text-white capitalize">{order.fulfillmentStatus}</p>
            </div>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Left: Items + Tickets */}
        <div className="lg:col-span-2 space-y-6">
          {/* Order Items */}
          <div className="card overflow-hidden">
            <div className="p-6 border-b border-gray-200 dark:border-gray-700">
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white">Order Items</h3>
            </div>
            {order.items && order.items.length > 0 ? (
              <div className="table-container">
                <table className="table">
                  <thead className="table-header">
                    <tr>
                      <th className="table-header-cell">Item</th>
                      <th className="table-header-cell">Qty</th>
                      <th className="table-header-cell">Unit Price</th>
                      <th className="table-header-cell">Total</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
                    {order.items.map((item) => (
                      <tr key={item.id} className="table-row">
                        <td className="table-cell">
                          <p className="font-medium text-gray-900 dark:text-white">{item.itemName}</p>
                          {item.ticketType && (
                            <p className="text-xs text-gray-500 dark:text-gray-400">{item.ticketType.name}</p>
                          )}
                        </td>
                        <td className="table-cell">{item.quantity}</td>
                        <td className="table-cell">{formatCurrency(item.unitPrice, item.currency)}</td>
                        <td className="table-cell font-medium">{formatCurrency(item.totalAmount, item.currency)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            ) : (
              <div className="p-8 text-center text-gray-400">No order items</div>
            )}

            {/* Totals */}
            <div className="p-6 border-t border-gray-200 dark:border-gray-700 space-y-2">
              <div className="flex justify-between text-sm">
                <span className="text-gray-500 dark:text-gray-400">Subtotal</span>
                <span className="text-gray-900 dark:text-white">{formatCurrency(order.subtotal, order.currency)}</span>
              </div>
              {order.discountAmount > 0 && (
                <div className="flex justify-between text-sm">
                  <span className="text-gray-500 dark:text-gray-400">
                    Discount {order.couponCode && <span className="text-xs">({order.couponCode})</span>}
                  </span>
                  <span className="text-success-600">-{formatCurrency(order.discountAmount, order.currency)}</span>
                </div>
              )}
              {order.taxAmount > 0 && (
                <div className="flex justify-between text-sm">
                  <span className="text-gray-500 dark:text-gray-400">Tax</span>
                  <span className="text-gray-900 dark:text-white">{formatCurrency(order.taxAmount, order.currency)}</span>
                </div>
              )}
              {order.feesAmount > 0 && (
                <div className="flex justify-between text-sm">
                  <span className="text-gray-500 dark:text-gray-400">Fees</span>
                  <span className="text-gray-900 dark:text-white">{formatCurrency(order.feesAmount, order.currency)}</span>
                </div>
              )}
              <div className="flex justify-between text-base font-semibold pt-2 border-t border-gray-200 dark:border-gray-700">
                <span className="text-gray-900 dark:text-white">Total</span>
                <span className="text-gray-900 dark:text-white">{formatCurrency(order.totalAmount, order.currency)}</span>
              </div>
            </div>
          </div>

          {/* Issued Tickets */}
          {order.tickets && order.tickets.length > 0 && (
            <div className="card overflow-hidden">
              <div className="p-6 border-b border-gray-200 dark:border-gray-700">
                <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
                  Issued Tickets ({order.tickets.length})
                </h3>
              </div>
              <div className="table-container">
                <table className="table">
                  <thead className="table-header">
                    <tr>
                      <th className="table-header-cell">Ticket #</th>
                      <th className="table-header-cell">Attendee</th>
                      <th className="table-header-cell">Type</th>
                      <th className="table-header-cell">Status</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
                    {order.tickets.map((ticket) => (
                      <tr key={ticket.id} className="table-row">
                        <td className="table-cell font-mono text-sm">{ticket.ticketNumber}</td>
                        <td className="table-cell">
                          <p className="text-sm text-gray-900 dark:text-white">{ticket.attendeeName || '-'}</p>
                          <p className="text-xs text-gray-500 dark:text-gray-400">{ticket.attendeeEmail || ''}</p>
                        </td>
                        <td className="table-cell">{ticket.ticketType?.name || '-'}</td>
                        <td className="table-cell">
                          <span className={cn('badge', getStatusColor(ticket.status))}>{ticket.status}</span>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </div>

        {/* Right Sidebar */}
        <div className="space-y-6">
          {/* Customer Info */}
          <div className="card p-6">
            <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">Customer</h3>
            <div className="space-y-3">
              <div className="flex items-center gap-3">
                <User className="w-4 h-4 text-gray-400" />
                <span className="text-sm text-gray-900 dark:text-white">{order.buyerName}</span>
              </div>
              <div className="flex items-center gap-3">
                <Mail className="w-4 h-4 text-gray-400" />
                <span className="text-sm text-gray-900 dark:text-white">{order.buyerEmail}</span>
              </div>
              {order.buyerPhone && (
                <div className="flex items-center gap-3">
                  <Phone className="w-4 h-4 text-gray-400" />
                  <span className="text-sm text-gray-900 dark:text-white">{order.buyerPhone}</span>
                </div>
              )}
            </div>
          </div>

          {/* Event Info */}
          {order.event && (
            <div className="card p-6">
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">Event</h3>
              <Link
                to={`/events/${order.event.id}`}
                className="flex items-start gap-3 hover:bg-gray-50 dark:hover:bg-gray-700/50 -mx-2 px-2 py-2 rounded-lg transition-colors"
              >
                <Calendar className="w-5 h-5 text-primary-500 mt-0.5" />
                <div>
                  <p className="text-sm font-medium text-gray-900 dark:text-white">{order.event.name}</p>
                  <p className="text-xs text-gray-500 dark:text-gray-400">{formatDate(order.event.startDate)}</p>
                </div>
              </Link>
            </div>
          )}

          {/* Payment Details */}
          <div className="card p-6">
            <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">Payment</h3>
            <div className="space-y-3 text-sm">
              <div className="flex justify-between">
                <span className="text-gray-500 dark:text-gray-400">Status</span>
                <span className={cn('badge', getStatusColor(order.paymentStatus))}>{order.paymentStatus}</span>
              </div>
              {order.paidAt && (
                <div className="flex justify-between">
                  <span className="text-gray-500 dark:text-gray-400">Paid At</span>
                  <span className="text-gray-900 dark:text-white">{formatDateTime(order.paidAt)}</span>
                </div>
              )}
              {order.gatewayTransactionId && (
                <div className="flex justify-between">
                  <span className="text-gray-500 dark:text-gray-400">Transaction ID</span>
                  <span className="text-gray-900 dark:text-white font-mono text-xs">{order.gatewayTransactionId}</span>
                </div>
              )}
              {order.refundedAmount > 0 && (
                <div className="flex justify-between">
                  <span className="text-gray-500 dark:text-gray-400">Refunded</span>
                  <span className="text-danger-600">{formatCurrency(order.refundedAmount, order.currency)}</span>
                </div>
              )}
              <div className="flex justify-between">
                <span className="text-gray-500 dark:text-gray-400">Source</span>
                <span className="text-gray-900 dark:text-white capitalize">{order.source || 'web'}</span>
              </div>
            </div>
          </div>

          {/* Notes */}
          {order.notes && (
            <div className="card p-6">
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">Notes</h3>
              <p className="text-sm text-gray-600 dark:text-gray-300 whitespace-pre-wrap">{order.notes}</p>
            </div>
          )}
        </div>
      </div>
    </motion.div>
  );
}
